import {
  WeightSetData,
  ResistanceBandSetData,
  TimedSetData,
} from "../types/program";
import { validationRules, validateField } from "./validation";

// Pure helpers behind the per-set editor in SortableExerciseItem. Kept out of
// the component so the rules can be pinned by tests without rendering.

export type SetsData = Record<string, any>;

export interface ProcessedSetField {
  /** Value to store in sets_data for this field. */
  value: any;
  /** Validation message, or null when the value is acceptable. */
  error: string | null;
}

/**
 * Whole positive integer that must be present - used by reps and duration.
 * An empty input is stored as "" so the field visibly clears; a non-numeric or
 * sub-1 input is stored as typed so the user can see what was rejected.
 */
const requiredPositiveInt = (value: any, label: string): ProcessedSetField => {
  if (value === "" || value === null || value === undefined) {
    return { value: "", error: `${label} is required` };
  }
  const num = parseInt(value);
  if (!isNaN(num) && num >= 1) {
    return { value: num, error: null };
  }
  return { value, error: `${label} must be at least 1` };
};

/** Coerce and validate one field of one set for the given training method. */
export const processSetField = (
  trainingMethod: string,
  field: string,
  value: any
): ProcessedSetField => {
  if (trainingMethod === "weight") {
    if (field === "reps") return requiredPositiveInt(value, "Reps");

    if (field === "weight") {
      let processed: number | null;
      if (value === "" || value === null || value === undefined) {
        processed = null;
      } else {
        const num = parseFloat(value);
        processed = !isNaN(num) ? Math.max(1, num) : null;
      }
      return {
        value: processed,
        error: validateField(processed?.toString() || "", "Weight", {
          required: false,
          rules: [validationRules.minWeight()],
        }),
      };
    }
  } else if (trainingMethod === "resistance_band") {
    if (field === "reps") return requiredPositiveInt(value, "Reps");

    if (field === "resistance_level") {
      let processed: number | null;
      if (value === "" || value === null || value === undefined) {
        processed = null;
      } else {
        const num = parseInt(value);
        processed = !isNaN(num) ? num : null;
      }
      return {
        value: processed,
        error: validateField(processed?.toString() || "", "Resistance Level", {
          required: false,
          rules: [],
        }),
      };
    }
  } else if (trainingMethod === "timed") {
    if (field === "duration_seconds") {
      return requiredPositiveInt(value, "Duration");
    }
  }

  // Unknown field for this method: store as-is, no validation.
  return { value, error: null };
};

/** The blank set appended by "Add Set" for a training method. */
export const newSetForTrainingMethod = (
  trainingMethod: string
): WeightSetData | ResistanceBandSetData | TimedSetData | undefined => {
  if (trainingMethod === "weight") {
    return { reps: 1, weight: null, completed: false } as WeightSetData;
  }
  if (trainingMethod === "resistance_band") {
    return {
      reps: 1,
      resistance_level: null,
      completed: false,
    } as ResistanceBandSetData;
  }
  if (trainingMethod === "timed") {
    return { duration_seconds: 30, completed: false } as TimedSetData;
  }
  return undefined;
};

/** Drop one set and renumber the rest as set_1..set_n in key order. */
export const removeSetAndRenumber = (
  setsData: SetsData,
  setKey: string
): SetsData => {
  const remaining = { ...setsData };
  delete remaining[setKey];

  const reordered: SetsData = {};
  Object.keys(remaining)
    .sort()
    .forEach((key, index) => {
      reordered[`set_${index + 1}`] = remaining[key];
    });
  return reordered;
};

/** Every validation-error key a set can own, for clearing on removal. */
export const SET_FIELD_NAMES = [
  "reps",
  "weight",
  "resistance_level",
  "duration_seconds",
] as const;
